import {messages} from '@smartmixin/i18n';
import {ArrowLeft, House, Question} from '@phosphor-icons/react';
import {
  alpha,
  Box,
  Breadcrumbs,
  ButtonBase,
  Grid,
  Link,
  Typography,
  useTheme,
} from '@mui/material';
import {useIntl} from 'react-intl';
import {useNavigate, useParams} from 'react-router';
import RouterLink from '../../../lib/RouterLink';
import AppPaper from '../../../components/AppPaper';
import EmptyState from '../../../components/EmptyState';
import {
  CATEGORY_COLORS,
  CATEGORY_ICONS,
  CATEGORY_MESSAGES,
  getReadingTime,
} from '../../../help-center/constants';
import {useArticles} from '../../../help-center/hooks/useArticles';
import {
  useHelpAlternates,
  useHelpBasePath,
} from '../../../help-center/hooks/useHelpLocale';
import {Article} from '../../../help-center/types';
import SmartMixinHelmet from '../../../lib/SmartMixinHelmet';
import SearchBar from '../../../help-center/components/SearchBar';

type ArticleCardProps = {
  article: Article;
};

const ArticleCard = ({article}: ArticleCardProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const {formatMessage} = useIntl();
  const helpBasePath = useHelpBasePath();
  const readingTime = getReadingTime(article.content);

  return (
    <ButtonBase
      onClick={() => navigate(`${helpBasePath}/${article.slug}`)}
      sx={{
        width: '100%',
        textAlign: 'left',
        borderRadius: 2,
      }}>
      <AppPaper
        sx={{
          p: 2.5,
          width: '100%',
          height: '100%',
          transition: 'all 200ms ease',
          '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, 0.02),
          },
        }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            mb: 1,
          }}>
          {article.title}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
          }}>
          {formatMessage(messages.common.minRead, {minutes: readingTime})}
        </Typography>
      </AppPaper>
    </ButtonBase>
  );
};

const Component = () => {
  const theme = useTheme();
  const {category} = useParams<{category: string}>();
  const {articlesByCategory} = useArticles();
  const {formatMessage} = useIntl();
  const helpBasePath = useHelpBasePath();
  const alternates = useHelpAlternates();

  const helpCenterTitle = formatMessage(messages.common.helpCenter);
  const articles = category ? articlesByCategory[category] : undefined;

  const categoryLabel =
    category && CATEGORY_MESSAGES[category]
      ? formatMessage(CATEGORY_MESSAGES[category])
      : category;

  const Icon = category ? CATEGORY_ICONS[category] || Question : Question;
  const color = category
    ? CATEGORY_COLORS[category] || theme.palette.primary.main
    : theme.palette.primary.main;

  if (!articles || articles.length === 0) {
    return (
      <>
        <SmartMixinHelmet
          title={`${categoryLabel || ''} - ${helpCenterTitle}`}
        />
        <EmptyState
          icon={<Question />}
          title={formatMessage(messages.common.categoryNotFound)}
          description={formatMessage(
            messages.common.categoryNotFoundDescription,
          )}
          action={{
            label: formatMessage(messages.common.backToHelpCenter),
            onClick: () => (window.location.href = helpBasePath),
          }}
          size="large"
        />
      </>
    );
  }

  return (
    <>
      <SmartMixinHelmet
        title={`${categoryLabel} - ${helpCenterTitle}`}
        alternates={alternates}
      />
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{mb: 3}} separator="›">
        <Link
          component={RouterLink}
          to={helpBasePath}
          underline="hover"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color: 'text.secondary',
            '&:hover': {color: 'primary.main'},
          }}>
          <House size={18} />
          {helpCenterTitle}
        </Link>
        <Typography
          sx={{
            color: 'text.primary',
          }}>
          {categoryLabel}
        </Typography>
      </Breadcrumbs>
      {/* Search bar */}
      <SearchBar />
      {/* Category header */}
      <AppPaper sx={{p: 3, mb: 3}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              backgroundColor: alpha(color, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <Icon size={28} color={color} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{fontWeight: 700}}>
              {categoryLabel}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}>
              {formatMessage(messages.common.articleCount, {
                count: articles.length,
              })}
            </Typography>
          </Box>
        </Box>
      </AppPaper>
      {/* Articles grid */}
      <Grid data-testid="help-category-articles" container spacing={2}>
        {articles.map((article) => (
          <Grid key={article.id} size={{xs: 12, sm: 6, md: 4}}>
            <ArticleCard article={article} />
          </Grid>
        ))}
      </Grid>
      {/* Back link */}
      <Box sx={{mt: 4, textAlign: 'center'}}>
        <Link
          component={RouterLink}
          to={helpBasePath}
          underline="hover"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            color: 'primary.main',
            fontWeight: 500,
          }}>
          <ArrowLeft size={18} />
          {formatMessage(messages.common.backToHelpCenter)}
        </Link>
      </Box>
    </>
  );
};

Component.displayName = 'CategoryScreen';

export {Component};
